// Shared font-face declarations and base styles used by all views.

export const fontFaces = `
@font-face {
    font-family: 'Berkeley Mono';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(/fonts/BerkeleyMono-Regular.woff2) format('woff2');
}

@font-face {
    font-family: 'Fira Code';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(/fonts/FiraCode-Regular.woff2) format('woff2');
}

@font-face {
    font-family: 'Source Code Pro';
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(/fonts/SourceCodePro-Regular.woff2) format('woff2');
}
`

export const fonts: { label: string; value: string }[] = [
    { label: "Berkeley Mono", value: "'Berkeley Mono', monospace" },
    { label: "Fira Code", value: "'Fira Code', monospace" },
    { label: "Source Code Pro", value: "'Source Code Pro', monospace" },
    { label: "System", value: "ui-monospace, SFMono-Regular, Menlo, monospace" },
]

const injected = new Set<string>()

// Inject a CSS string into the document head, deduplicating by key.
export function injectStyle(key: string, css: string) {
    if (injected.has(key)) return
    const style = document.createElement("style")
    style.textContent = css
    document.head.appendChild(style)
    injected.add(key)
}

// Apply saved theme and font preferences to a container element.
export function applyPreferences(container: HTMLElement) {
    const savedScheme = localStorage.getItem("code-viewer-theme")
    if (savedScheme) {
        container.style.colorScheme = savedScheme
    }
    const savedFont = localStorage.getItem("code-viewer-font")
    const match = fonts.find((f) => f.label === savedFont)
    if (match) {
        container.style.fontFamily = match.value
    }
}

const shellCss = `
${fontFaces}

.page-shell {
    position: relative;
    color-scheme: light dark;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 14px;
    line-height: 1.5;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: light-dark(#fff, #0d1117);
    color: light-dark(#24292f, #e6edf3);
}

.page-shell-controls {
    display: flex;
    gap: 8px;
    padding: 6px 12px;
    border-bottom: 1px solid light-dark(#d0d7de, #30363d);
    background: light-dark(#f6f8fa, #161b22);
    color: light-dark(#24292f, #e6edf3);
    flex-wrap: wrap;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 1;
}

.page-shell-controls a {
    color: light-dark(#0969da, #58a6ff);
    text-decoration: none;
}

.page-shell-controls a:hover {
    text-decoration: underline;
}

.page-shell-controls a.logo-link {
    color: light-dark(#24292f, #e6edf3);
}

.page-shell-controls a.logo-link:hover {
    text-decoration: none;
}

.page-shell-controls button {
    padding: 2px 10px;
    border: 1px solid light-dark(#d0d7de, #30363d);
    border-radius: 6px;
    background: light-dark(#fff, #21262d);
    cursor: pointer;
    font-size: 12px;
    line-height: 1.5;
    color: light-dark(#24292f, #e6edf3);
}

.page-shell-controls button:hover {
    background: light-dark(#f3f4f6, #30363d);
}

.page-shell-controls button.active {
    background: light-dark(#0969da, #58a6ff);
    color: light-dark(#fff, #0d1117);
    border-color: light-dark(#0969da, #58a6ff);
}

.page-shell-controls .extension-badges {
    display: flex;
    gap: 6px;
    align-items: center;
}

.page-shell-controls .extension-badges img {
    height: 20px;
    display: block;
}

.page-shell-content {
    flex: 1;
    display: flex;
    flex-direction: column;
}
`

interface PageShellOptions {
    header?: HTMLElement
    extraControls?: (controls: HTMLElement, container: HTMLElement) => void
}

// Create a page shell with a sticky controls bar (breadcrumb + theme/font toggles).
// Returns { container, content } where content is the area below the controls bar.
export function createPageShell({ header, extraControls }: PageShellOptions): { container: HTMLElement; content: HTMLElement } {
    injectStyle("page-shell", shellCss)

    const container = document.createElement("div")
    container.className = "page-shell"
    applyPreferences(container)

    // Controls bar.
    const controls = document.createElement("div")
    controls.className = "page-shell-controls"

    if (header) {
        const label = document.createElement("span")
        label.style.fontWeight = "600"
        label.style.marginRight = "auto"
        label.appendChild(header)
        controls.appendChild(label)
    }

    // Extension install badges.
    const badges = document.createElement("span")
    badges.className = "extension-badges"
    const chromeLink = document.createElement("a")
    chromeLink.href = "https://chromewebstore.google.com/detail/pkggeomysdev-source-links/kehcbeihpiighnghampfaiapahlbjlgl"
    chromeLink.target = "_blank"
    const chromePicture = document.createElement("picture")
    const chromeDarkSource = document.createElement("source")
    chromeDarkSource.srcset = "/assets/chrome-web-store-badge-border.png"
    chromeDarkSource.media = "(prefers-color-scheme: dark)"
    chromePicture.appendChild(chromeDarkSource)
    const chromeImg = document.createElement("img")
    chromeImg.src = "/assets/chrome-web-store-badge.png"
    chromeImg.alt = "Chrome Web Store"
    chromePicture.appendChild(chromeImg)
    chromeLink.appendChild(chromePicture)
    badges.appendChild(chromeLink)
    const firefoxLink = document.createElement("a")
    firefoxLink.href = "https://addons.mozilla.org/en-US/firefox/addon/geomys-source-links/"
    firefoxLink.target = "_blank"
    const firefoxPicture = document.createElement("picture")
    const firefoxDarkSource = document.createElement("source")
    firefoxDarkSource.srcset = "/assets/firefox-addon-badge-light.svg"
    firefoxDarkSource.media = "(prefers-color-scheme: dark)"
    firefoxPicture.appendChild(firefoxDarkSource)
    const firefoxImg = document.createElement("img")
    firefoxImg.src = "/assets/firefox-addon-badge-dark.svg"
    firefoxImg.alt = "Firefox Add-on"
    firefoxPicture.appendChild(firefoxImg)
    firefoxLink.appendChild(firefoxPicture)
    badges.appendChild(firefoxLink)
    controls.appendChild(badges)

    // Let the caller add view-specific buttons before theme/font.
    if (extraControls) {
        extraControls(controls, container)
    }

    // Theme toggle cycles: auto → light → dark → auto.
    const schemes = ["light dark", "light", "dark"] as const
    const labels = ["Auto color scheme", "Light", "Dark"] as const
    const savedScheme = localStorage.getItem("code-viewer-theme")
    let schemeIndex = Math.max(0, schemes.indexOf(savedScheme as typeof schemes[number]))
    container.style.colorScheme = schemes[schemeIndex]
    const themeBtn = document.createElement("button")
    themeBtn.textContent = labels[schemeIndex]
    themeBtn.addEventListener("click", () => {
        schemeIndex = (schemeIndex + 1) % schemes.length
        container.style.colorScheme = schemes[schemeIndex]
        themeBtn.textContent = labels[schemeIndex]
        localStorage.setItem("code-viewer-theme", schemes[schemeIndex])
    })
    controls.appendChild(themeBtn)

    // Font toggle cycles through available monospace fonts.
    const savedFont = localStorage.getItem("code-viewer-font")
    let fontIndex = Math.max(0, fonts.findIndex((f) => f.label === savedFont))
    container.style.fontFamily = fonts[fontIndex].value
    const fontBtn = document.createElement("button")
    fontBtn.textContent = fonts[fontIndex].label
    fontBtn.addEventListener("click", () => {
        fontIndex = (fontIndex + 1) % fonts.length
        container.style.fontFamily = fonts[fontIndex].value
        fontBtn.textContent = fonts[fontIndex].label
        localStorage.setItem("code-viewer-font", fonts[fontIndex].label)
    })
    controls.appendChild(fontBtn)

    // "by Geomys" logo link.
    const logoLink = document.createElement("a")
    logoLink.href = "https://geomys.org"
    logoLink.style.display = "flex"
    logoLink.style.alignItems = "center"
    logoLink.style.gap = "5px"
    logoLink.style.marginLeft = "4px"
    logoLink.className = "logo-link"
    const byLabel = document.createElement("span")
    byLabel.textContent = "by"
    byLabel.style.fontSize = "12px"
    logoLink.appendChild(byLabel)
    const logoPicture = document.createElement("picture")
    const darkSource = document.createElement("source")
    darkSource.srcset = "/assets/geomys_horizontal_white_alpha.png"
    darkSource.media = "(prefers-color-scheme: dark)"
    logoPicture.appendChild(darkSource)
    const logoImg = document.createElement("img")
    logoImg.src = "/assets/geomys_horizontal_black.png"
    logoImg.alt = "Geomys"
    logoImg.style.height = "20px"
    logoPicture.appendChild(logoImg)
    logoLink.appendChild(logoPicture)
    controls.appendChild(logoLink)

    container.appendChild(controls)

    const content = document.createElement("div")
    content.className = "page-shell-content"
    container.appendChild(content)

    return { container, content }
}
